/**
 * List of hardcode collection slugs
 * These pages have custom layouts and support concat slug in URL
 */
export const HARDCODE_COLLECTION_SLUGS = [
  'gym',
  'underwear',
  'excool',
  'graphic-tee',
  'father-s-day',
  'bra-legging-collection',
  'pickleball-tennis',
  'short-summer',
  'uv-protection',
  'vital-seamless',
  'vent-tech-new',
  'tshirt-polo-new',
  'racquet-sports',
  'outdoor-collection',
  'do-boi-nam',
  'do-thu-dong',
  'new-swimming',
  'football',
  'quan-ao-chay-bo',
  'lenzing-modal',
  'coolmate-activewear-nu',
  'coolmate-x-nguyen-thi-oanh',
  'amazon-best-seller',
  'copper-denim',
  'care-and-share',
  'ldp-promax',
  'viet-devils',
  'flash-sale',
  'cung-mien-trung-vung-vang',
  'buy-1-get-1-in-gift-shop',
  'buy-1-get-1-value-match',
  'black-friday',
  'gian-hang-qua-tang',
  'noel',
  'gift-guide',
  'nam-moi-len-do-moi',
  'new-year-campaign',
  'disney-tet-collection-2026',
  'new-customers',
  'coolmate-7th-birthday',
  'quan-lot-nam',
  'anti-uv-collection',
  'zero-mark-collection',
  'run-safe-in-the-dark',
  'seamless-motion',
  'hello-kitty',
  'phuc-huynh-x-coolmate',
] as const

export type HardcodeCollectionSlug = (typeof HARDCODE_COLLECTION_SLUGS)[number]

/**
 * List of special rewritten collection URLs and collection slugs that should NOT concat slug
 * These URLs/slugs should only use query params for filters, not concat in slug
 * - Special rewritten URLs: pathnames from rewrite rules (e.g., 'sieu-sale-ngay-doi-double-day')
 * - Collection slugs: direct collection slugs that should prevent concat (e.g., 'buy-1-get-1-in-gift-shop', 'gian-hang-qua-tang')
 * HOÀN TOÀN TÁCH BIỆT với HARDCODE_COLLECTION_SLUGS
 * Example: 'sieu-sale-ngay-doi-double-day' is rewritten to '/collection/flash-sale'
 */
export const SPECIAL_REWRITTEN_COLLECTION_URLS = [
  'sieu-sale-ngay-doi-double-day',
  'buy-1-get-1-in-gift-shop',
  'black-friday',
  'buy-1-get-1-value-match',
  'gian-hang-qua-tang',
  'gift-guide',
  'cool-set',
  'nam-moi-len-do-moi',
  'new-year-campaign',
  'disney-tet-collection-2026',
  'new-customers',
  'coolmate-7th-birthday',
] as const

/**
 * Check if a pathname or slug is a special rewritten collection URL
 * These URLs/slugs should NOT concat slug, only use query params for filters
 * @param pathnameOrSlug - URL pathname (can include locale and /collection/ prefix) or collection slug
 * @returns True if the pathname/slug is a special rewritten collection URL
 */
export function isSpecialRewrittenCollectionUrl(
  pathnameOrSlug: string | undefined
): boolean {
  if (!pathnameOrSlug) return false

  // Check if pathname contains any special rewritten URL
  // This works for both:
  // - Original pathname (sieu-sale-ngay-doi-double-day)
  // - Pathname with locale prefix (/vi/sieu-sale-ngay-doi-double-day)
  // - Collection slug directly (buy-1-get-1-in-gift-shop, gian-hang-qua-tang)
  return SPECIAL_REWRITTEN_COLLECTION_URLS.some((specialUrl) =>
    pathnameOrSlug.includes(specialUrl)
  )
}

/**
 * Check if a slug or path is a hardcode collection
 * Updated to use startsWith for more precise matching
 */
export function isHardcodeCollection(slugOrPath: string): boolean {
  // Clean slug (remove locale and /collection/ prefix if present)
  const cleanSlug = slugOrPath.replace(/^\/?(en|vi)?\/?collection\//, '')

  // Check if it starts with any hardcode slug (sorted by length to match longest first)
  const sortedSlugs = [...HARDCODE_COLLECTION_SLUGS].sort(
    (a, b) => b.length - a.length
  )

  return sortedSlugs.some(
    (hardcodeSlug) =>
      cleanSlug === hardcodeSlug || cleanSlug.startsWith(hardcodeSlug + '-')
  )
}

export enum ECollectionSort {
  DEFAULT = 'default',
  NEW = 'new',
  BESTSELLER = 'bestseller',
  PRICE_ASC = 'price-asc',
  PRICE_DESC = 'price-desc',
  PERCENT = 'percent',
}

export const COLLECTION_SORT_LABELS: Record<ECollectionSort, string> = {
  [ECollectionSort.DEFAULT]: 'default',
  [ECollectionSort.BESTSELLER]: 'bestseller',
  [ECollectionSort.NEW]: 'new',
  [ECollectionSort.PRICE_ASC]: 'price_asc',
  [ECollectionSort.PRICE_DESC]: 'price_desc',
  [ECollectionSort.PERCENT]: 'percent',
}

/**
 * List of collection slugs that should hide sorting functionality
 */
export const COLLECTIONS_WITHOUT_SORTING = [
  'san-pham-moi',
  'ban-chay-nhat',
  'shop-all',
  'gian-hang-qua-tang',
] as const

/**
 * Check if sorting should be hidden for a given collection slug
 */
export function shouldHideSorting(slug: string | undefined): boolean {
  if (!slug) return false
  return COLLECTIONS_WITHOUT_SORTING.includes(
    slug as (typeof COLLECTIONS_WITHOUT_SORTING)[number]
  )
}

/**
 * URL parameters that should be preserved when filtering
 * These params are not filter-related and should persist through filter operations
 */
export const PRESERVED_URL_PARAMS = {
  MAIN_PRODUCT_ID: 'main_product_id',
  PRIORITY_REGULAR_PRICE: 'priority_regular_price',
} as const

/**
 * Get preserved URL params for a specific collection
 * Different collections may need different preserved params
 * @param slug - Collection slug
 * @returns Array of param names to preserve
 */
export function getPreservedParamsForCollection(
  slug: string | undefined
): string[] {
  if (!slug) return []

  // Gift shop collection needs main_product_id and priority_regular_price preserved
  if (slug === 'gian-hang-qua-tang') {
    return [
      PRESERVED_URL_PARAMS.MAIN_PRODUCT_ID,
      PRESERVED_URL_PARAMS.PRIORITY_REGULAR_PRICE,
    ]
  }

  // Add more collections and their preserved params here if needed
  return []
}

/**
 * Defines pairs of facet filter keys that are mutually exclusive
 * When any value in one facet is selected, all values in the other facet will be automatically cleared
 * Format: [facet1_seo_alias, facet2_seo_alias]
 *
 * Example: ['danh-muc', 'sub-danh-muc']
 * - If user selects any value in 'danh-muc' facet → clear all values in 'sub-danh-muc' facet
 * - If user selects any value in 'sub-danh-muc' facet → clear all values in 'danh-muc' facet
 */
export const MUTUALLY_EXCLUSIVE_FACET_PAIRS: Array<[string, string]> = [
  ['danh-muc', 'sub-danh-muc'],
] as const

/**
 * Get the mutually exclusive partner for a given facet key
 * @param facetKey - The seo_alias of the facet
 * @returns The seo_alias of the mutually exclusive partner, or null if none
 */
export function getMutuallyExclusivePartner(facetKey: string): string | null {
  for (const [facet1, facet2] of MUTUALLY_EXCLUSIVE_FACET_PAIRS) {
    if (facet1 === facetKey) return facet2
    if (facet2 === facetKey) return facet1
  }
  return null
}

export const sortedAttributeCardsUnderwear = [
  {
    _id: 'brief-tam-giac',
    title: 'collection_page.card_underwear.brief_tam_giac.label',
    sub_title: 'collection_page.card_underwear.brief_tam_giac.description',
    icon: '',
    seo_alias: 'brief-tam-giac',
    product_attribute_id: '',
    updated_at: new Date(),
    created_at: new Date(),
    image: '/image/April2024/mceclip0_74.png',
    image_hover: '/image/August2023/mceclip1_68.png',
    short_description: null,
    sort: 0,
  },
  {
    _id: 'boxer-trunk',
    title: 'collection_page.card_underwear.boxer_trunk.label',
    sub_title: 'collection_page.card_underwear.boxer_trunk.description',
    icon: '',
    seo_alias: 'boxer-trunk',
    product_attribute_id: '',
    updated_at: new Date(),
    created_at: new Date(),
    sort: 0,
    image: '/image/April2024/mceclip5_54.png',
    image_hover: '/image/August2023/mceclip4_52.png',
    short_description: null,
  },

  {
    _id: 'brief-boxer-dang-dai',
    title: 'collection_page.card_underwear.brief_boxer_dang_dai.label',
    sub_title:
      'collection_page.card_underwear.brief_boxer_dang_dai.description',
    icon: '',
    seo_alias: 'brief-boxer-dang-dai',
    product_attribute_id: '',
    updated_at: new Date(),
    created_at: new Date(),
    image: '/image/April2024/mceclip4_46.png',
    image_hover: '/image/August2023/mceclip6_22.png',
    short_description: null,
    sort: 0,
  },

  {
    _id: 'boxer-shorts',
    title: 'collection_page.card_underwear.boxer_shorts.label',
    sub_title: 'collection_page.card_underwear.boxer_shorts.description',
    icon: '',
    seo_alias: 'boxer-shorts',
    product_attribute_id: '',
    updated_at: new Date(),
    created_at: new Date(),
    image: '/image/April2024/mceclip3_56.png',
    image_hover: '/image/October2023/mceclip0_80.png',
    short_description: null,
    sort: 0,
  },
  {
    _id: 'long-leg',
    title: 'collection_page.card_underwear.long_leg.label',
    sub_title: 'collection_page.card_underwear.long_leg.description',
    icon: '',
    seo_alias: 'long-leg',
    product_attribute_id: '',
    updated_at: new Date(),
    created_at: new Date(),
    image: '/image/April2024/mceclip2_84.png',
    image_hover: '/image/September2023/mceclip14.jpg',
    short_description: null,
    sort: 0,
  },
]
